import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import {
    Box, 
    Typography, 
    Button, 
    CircularProgress,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
// import { buildPermissionMap } from 'state/checkPermission';
import { buildValidationSchema } from 'components/dynamicUI/buildValidationSchema';
import { buildInitialValues } from 'components/dynamicUI/buildInitialValues';
import DynamicForm from 'components/dynamicUI/DynamicForm';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import StyledButton from 'components/StyledButton';
import FlexCenter from 'components/FlexCenter';


const DynamicEdit = () => {
    useAuthCheck();
    const { collection, recordId } = useParams();
    const navigate = useNavigate();

    const isEdit = Boolean(recordId);

    // const user = useSelector((state) => state.user);
    // const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
    // const canSave = useMemo(() => {
    //     const specific = permissionMap.get(collection?.toLowerCase());
    //     const generic  = permissionMap.get('modules');
    //     const e = specific || generic;
    //     return e?.has(isEdit ? 'update' : 'create') ?? false;
    // }, [permissionMap, collection, isEdit]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [collectionDef, setCollectionDef] = useState(null);
    const [fields, setFields] = useState([]);
    const [record, setRecord] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                setError(null);

                const { data: defData } = await api.get(`/collectiondefs/by-name/${collection}`);
                setCollectionDef(defData.collectionDef);
                setFields(defData.fields);

                if (isEdit) {
                    const { data: rec } = await api.get(`/data/${defData.collectionDef.name}/${recordId}`);
                    setRecord(rec);
                }
            } catch (err) {
                setError(err);
                toast.error(err.response?.data?.message || 'Failed to load.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [collection, recordId, isEdit]);

    const validationSchema = useMemo(() => buildValidationSchema(fields), [fields]);
    const initialValues = useMemo(
        () => buildInitialValues(fields, record),
        [fields, record]
    );

    const handleSubmit = useCallback(async (values) => {
        if (!collectionDef) return;
        try {
            setSaving(true);
            const dataPath = `/data/${collectionDef.name}`;
            if (isEdit) {
                await api.put(`${dataPath}/update/${recordId}`, values);
                toast.success('Updated successfully.');
            } else {
                await api.post(`${dataPath}/new`, values);
                toast.success('Created successfully.');
            }
            navigate(`/modules/${collection}`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    }, [collectionDef, collection, isEdit, recordId, navigate]);

    if (loading) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <CircularProgress />
        </FlexCenter>
    );

    if (error) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <Typography color="error">{error?.message}</Typography>
        </FlexCenter>
    );

    if (!collectionDef) return null;


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title={collectionDef.label}
                    subtitle={isEdit ? 'Update' : 'Create'}
                />
                <StyledButton onClick={() => navigate(`/modules/${collection}`)}>
                    <ArrowBackIosNewIcon />
                </StyledButton>
            </FlexBetween>

            <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ 
                        values, 
                        errors, 
                        touched, 
                        handleChange, 
                        handleBlur, 
                        handleSubmit: formSubmit, 
                        setFieldValue 
                    }) => (
                        <form onSubmit={formSubmit}>
                            <DynamicForm
                                fields={fields}
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                setFieldValue={setFieldValue}
                            />

                            <Box mt={2}>
                                <Button
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    // disabled={saving || !canSave}
                                    disabled={saving}
                                    sx={{ height: 42 }}
                                >
                                    {saving ? <CircularProgress size={20} /> : isEdit ? 'Update' : 'Create'}
                                </Button>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Box>
        </Box>
    );
};

export default DynamicEdit;
